import { useState } from 'react'
import {X, ChevronLeft, ChevronRight, Link2, Check, Calendar, User, Heart, Share2} from 'lucide-react'
import type { NewsArticle } from '@/types'
import { lumi } from '@/lib/lumi'
import CommentThread from '@/components/CommentThread'
import {
  getDisplayedLikes,
  getDisplayedShares,
  formatCount,
  hasLikedArticle,
  setArticleLiked,
} from '@/lib/engagement'

/* ── Ícones SVG das redes sociais ── */
function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5 shrink-0" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  )
}

function FacebookIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5 shrink-0" aria-hidden="true">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  )
}

function TelegramIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5 shrink-0" aria-hidden="true">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
    </svg>
  )
}

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5 shrink-0" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
  )
}

interface NewsCardProps {
  article: NewsArticle
}

function formatDate(dateStr: string) {
  const date = new Date(dateStr)
  const days = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado']
  const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
  return {
    dayOfWeek: days[date.getDay()],
    day: String(date.getDate()).padStart(2, '0'),
    month: months[date.getMonth()],
    year: date.getFullYear(),
    full: date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }),
  }
}

/* ── Lightbox da galeria ── */
function GalleryLightbox({ images, startIndex, onClose }: { images: string[]; startIndex: number; onClose: () => void }) {
  const [current, setCurrent] = useState(startIndex)
  const prev = () => setCurrent(c => (c - 1 + images.length) % images.length)
  const next = () => setCurrent(c => (c + 1) % images.length)

  return (
    <div className="fixed inset-0 bg-black/95 z-[9999] flex items-center justify-center animate-scale-in" onClick={onClose}>
      <button onClick={onClose} className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10" aria-label="Fechar">
        <X size={20} />
      </button>
      {images.length > 1 && (
        <>
          <button onClick={e => { e.stopPropagation(); prev() }} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10" aria-label="Anterior">
            <ChevronLeft size={22} />
          </button>
          <button onClick={e => { e.stopPropagation(); next() }} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10" aria-label="Próxima">
            <ChevronRight size={22} />
          </button>
        </>
      )}
      <img src={images[current]} alt={`Galeria ${current + 1}`} className="max-w-[90vw] max-h-[85vh] object-contain rounded-xl select-none shadow-2xl" onClick={e => e.stopPropagation()} />
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-1.5">
        {images.map((_, idx) => (
          <button key={idx} onClick={e => { e.stopPropagation(); setCurrent(idx) }} className={`w-1.5 h-1.5 rounded-full transition-all ${idx === current ? 'bg-white w-4' : 'bg-white/40 hover:bg-white/70'}`} aria-label={`Foto ${idx + 1}`} />
        ))}
      </div>
    </div>
  )
}

export default function NewsCard({ article }: NewsCardProps) {
  const pubDate = article.publishedAt || article.createdAt
  const dateInfo = formatDate(pubDate)
  const gallery = article.galleryImages?.filter(Boolean) || []
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const startIso = article.publishedAt || article.createdAt
  const [realLikes, setRealLikes] = useState(article.likes || 0)
  const [realShares, setRealShares] = useState(article.shares || 0)
  const [liked, setLiked] = useState(() => hasLikedArticle(article._id))
  const [likeBusy, setLikeBusy] = useState(false)

  const persistEngagement = async (nextLikes: number, nextShares: number) => {
    await lumi.entities.news_articles.update(article._id, {
      likes: nextLikes,
      shares: nextShares,
      updatedAt: new Date().toISOString(),
    })
  }

  const toggleLike = async () => {
    if (likeBusy) return
    const nextLiked = !liked
    const nextLikes = Math.max(0, realLikes + (nextLiked ? 1 : -1))
    setLiked(nextLiked)
    setRealLikes(nextLikes)
    setArticleLiked(article._id, nextLiked)
    setLikeBusy(true)
    try {
      await persistEngagement(nextLikes, realShares)
    } catch (e) {
      console.error('Erro ao guardar curtida:', e)
      setLiked(!nextLiked)
      setRealLikes(realLikes)
      setArticleLiked(article._id, !nextLiked)
    } finally {
      setLikeBusy(false)
    }
  }

  const registerShare = async () => {
    const nextShares = realShares + 1
    setRealShares(nextShares)
    try {
      await persistEngagement(realLikes, nextShares)
    } catch (e) {
      console.error('Erro ao guardar partilha:', e)
      setRealShares(realShares)
    }
  }

  return (
    <article className="bg-white dark:bg-gray-900 rounded-2xl overflow-hidden shadow-sm border border-gray-100 dark:border-gray-800/60 card-lift animate-fade-up">

      {/* ── Imagem destacada ── */}
      <div className="relative w-full aspect-video bg-gray-100 dark:bg-gray-800">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.imageAlt || article.title}
            className="w-full h-full object-cover transition-transform duration-700 hover:scale-[1.02]"
            loading="lazy"
          />

          {/* Gradiente inferior para legibilidade */}
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />

          {/* Badge data + autor — compacto no mobile */}
          <div className="absolute top-2.5 left-2.5 sm:top-3 sm:left-3 flex rounded-xl overflow-hidden shadow-xl" style={{ backdropFilter: 'blur(8px)' }}>
            {/* Bloco data */}
            <div className="bg-gray-950/85 px-2 py-1 sm:px-2.5 sm:py-1.5 flex flex-col items-center justify-center min-w-[40px] sm:min-w-[52px]">
              <span className="text-yellow-400 text-[6px] sm:text-[8px] font-bold uppercase tracking-widest leading-none mb-0.5">{dateInfo.dayOfWeek}</span>
              <span className="text-white font-black text-xs sm:text-base leading-none tabular-nums">{dateInfo.day}/{String(new Date(pubDate).getMonth() + 1).padStart(2, '0')}</span>
              <span className="text-yellow-400 font-bold text-[8px] sm:text-[11px] leading-none mt-0.5">{dateInfo.year}</span>
            </div>
            {/* Divisor */}
            <div className="w-px bg-white/10" />
            {/* Bloco autor */}
            <div className="bg-gray-900/85 px-2 py-1 sm:px-2.5 sm:py-1.5 flex flex-col justify-center">
              <span className="text-yellow-400 text-[6px] sm:text-[8px] font-bold uppercase tracking-widest leading-none mb-0.5">Autor</span>
              <span className="text-white font-semibold text-[9px] sm:text-xs leading-none whitespace-nowrap max-w-[72px] sm:max-w-[110px] truncate">{article.authorName}</span>
            </div>
          </div>

          {/* Categoria no canto oposto */}
          {article.category ? (
            <div className="absolute top-2.5 right-2.5 sm:top-3 sm:right-3">
              <span className="category-pill text-[9px] sm:text-[11px] shadow-lg">{article.category}</span>
            </div>
          ) : null}
        </div>

        <CommentThread article={article} />
      </div>

      {/* ── Galeria de fotos ── */}
      {gallery.length > 0 && (
        <div className="px-5 sm:px-6 pt-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1 h-4 bg-red-600 rounded-full" />
            <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
              Galeria · {gallery.length} fotos
            </p>
          </div>
          <div className={`grid gap-2 ${gallery.length === 1 ? 'grid-cols-1' : gallery.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
            {gallery.map((url, idx) => (
              <button key={idx} type="button" onClick={() => setLightboxIndex(idx)}
                className="relative aspect-video overflow-hidden rounded-lg group focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                aria-label={`Ver foto ${idx + 1}`}>
                <img src={url} alt={`Galeria ${idx + 1}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors duration-300 rounded-lg" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Conteúdo editorial ── */}
      <div className="p-5 sm:p-7">

        {/* Título */}
        <h2 className="font-serif text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white leading-[1.25] tracking-tight mb-4">
          {article.title}
        </h2>

        {/* Meta linha */}
        <div className="flex items-center gap-4 mb-5 pb-5 border-b border-gray-100 dark:border-gray-800 flex-wrap">
          <span className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
            <User size={12} />
            <span className="font-medium text-gray-600 dark:text-gray-400">{article.authorName}</span>
          </span>
          <span className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
            <Calendar size={12} />
            <time dateTime={pubDate}>{dateInfo.full}</time>
          </span>
        </div>

        {/* Corpo da matéria */}
        <div
          className="prose prose-gray dark:prose-invert max-w-none prose-p:text-gray-700 dark:prose-p:text-gray-200 prose-p:leading-[1.85] prose-headings:font-serif prose-headings:tracking-tight prose-a:text-red-600 dark:prose-a:text-red-400 prose-a:no-underline hover:prose-a:underline prose-strong:text-gray-900 dark:prose-strong:text-white prose-blockquote:border-red-600 prose-blockquote:text-gray-600 dark:prose-blockquote:text-gray-300 prose-img:rounded-xl prose-code:text-red-600 dark:prose-code:text-red-400 text-base sm:text-[17px]"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <GalleryLightbox images={gallery} startIndex={lightboxIndex} onClose={() => setLightboxIndex(null)} />
      )}

      {/* ── Fonte da notícia ── */}
      {(article as NewsArticle & { source?: string }).source && (
        <div className="px-5 sm:px-7 pb-2 flex justify-end">
          <span className="text-[11px] text-gray-400 dark:text-gray-500 italic">
            Fonte: <strong>{(article as NewsArticle & { source?: string }).source}</strong>
          </span>
        </div>
      )}

      {/* ── Curtidas e partilhas ── */}
      <EngagementBar
        likes={getDisplayedLikes(realLikes, startIso)}
        shares={getDisplayedShares(realShares, startIso)}
        liked={liked}
        busy={likeBusy}
        onLike={toggleLike}
      />

      {/* ── Barra de compartilhamento ── */}
      <ShareBar article={article} onShare={registerShare} />
    </article>
  )
}

/* ── Curtidas + partilhas (reais + 1 a cada 2 horas) ── */
function EngagementBar({
  likes,
  shares,
  liked,
  busy,
  onLike,
}: {
  likes: number
  shares: number
  liked: boolean
  busy: boolean
  onLike: () => void
}) {
  return (
    <div className="mx-5 sm:mx-7 mb-3 flex items-center gap-2 flex-wrap">
      <button
        type="button"
        onClick={onLike}
        disabled={busy}
        aria-pressed={liked}
        aria-label={liked ? 'Remover curtida' : 'Curtir esta notícia'}
        className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-semibold border transition-all active:scale-95 ${
          liked
            ? 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800 text-red-600 dark:text-red-400'
            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-red-300 hover:text-red-600'
        }`}
      >
        <Heart size={15} className={liked ? 'fill-current' : ''} />
        <span>{formatCount(likes)}</span>
        <span className="text-[11px] font-medium opacity-70">{likes === 1 ? 'curtida' : 'curtidas'}</span>
      </button>
      <div className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-semibold border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300">
        <Share2 size={15} />
        <span>{formatCount(shares)}</span>
        <span className="text-[11px] font-medium opacity-70">{shares === 1 ? 'partilha' : 'partilhas'}</span>
      </div>
    </div>
  )
}

/* ── Barra de compartilhamento ── */
function ShareBar({ article, onShare }: { article: NewsArticle; onShare: () => void }) {
  const [copied, setCopied] = useState(false)
  const slug = article.slug || article._id

  /**
   * URL REAL da notícia na SPA — usada no botão "Copiar link"
   * e como destino final para utilizadores humanos.
   */
  const SITE_URL = 'https://carlosfelipeofera.com'
  const spaUrl = `${SITE_URL}/artigo/${encodeURIComponent(slug)}`

  /**
   * URL de partilha → endpoint articleOg
   *
   * Por que usar o endpoint e não a URL da SPA?
   * WhatsApp, Facebook, Telegram e X são bots — não executam JavaScript.
   * Quando recebem a URL da SPA (que é uma SPA/CSR), lêem um index.html
   * genérico sem as OG tags do artigo → mostram o ícone do site em vez
   * da imagem da notícia.
   *
   * O endpoint articleOg:
   *  1. Detecta crawlers pelo User-Agent
   *  2. Devolve HTML estático com og:image, og:title, og:description corretos
   *  3. Redireciona navegadores humanos automaticamente para a SPA
   *
   * Resultado: redes sociais mostram a imagem, título e descrição do artigo.
   */
  const PROJECT_ID = 'p454626448092758016'
  const ogEndpoint = `https://api.lumi.new/v1/functions/${PROJECT_ID}/articleOg?slug=${encodeURIComponent(slug)}`

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(ogEndpoint)}`
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(ogEndpoint)}`
  const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(ogEndpoint)}&text=${encodeURIComponent(article.title)}`
  const twitterUrl  = `https://twitter.com/intent/tweet?url=${encodeURIComponent(ogEndpoint)}&text=${encodeURIComponent(article.title)}`

  function handleCopy() {
    navigator.clipboard.writeText(spaUrl).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
      onShare()
    })
  }

  return (
    <div className="mx-5 sm:mx-7 mb-5 sm:mb-7 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/60 px-4 py-3">
      <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.12em] mb-2.5">
        Partilhar esta notícia
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" aria-label="Partilhar no WhatsApp" onClick={onShare}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-[#25D366] hover:bg-[#1ebe59] active:scale-95 transition-all shadow-sm">
          <WhatsAppIcon /><span>WhatsApp</span>
        </a>
        <a href={facebookUrl} target="_blank" rel="noopener noreferrer" aria-label="Partilhar no Facebook" onClick={onShare}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-[#1877F2] hover:bg-[#0e6add] active:scale-95 transition-all shadow-sm">
          <FacebookIcon /><span>Facebook</span>
        </a>
        <a href={telegramUrl} target="_blank" rel="noopener noreferrer" aria-label="Partilhar no Telegram" onClick={onShare}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-[#229ED9] hover:bg-[#1a8fbf] active:scale-95 transition-all shadow-sm">
          <TelegramIcon /><span>Telegram</span>
        </a>
        <a href={twitterUrl} target="_blank" rel="noopener noreferrer" aria-label="Partilhar no X" onClick={onShare}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-gray-900 dark:bg-gray-700 hover:bg-gray-800 dark:hover:bg-gray-600 active:scale-95 transition-all shadow-sm">
          <XIcon /><span>X</span>
        </a>
        <button type="button" onClick={handleCopy} aria-label="Copiar link"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all shadow-sm border active:scale-95 ${
            copied
              ? 'bg-green-50 dark:bg-green-900/30 border-green-300 dark:border-green-700 text-green-700 dark:text-green-400'
              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
          }`}>
          {copied ? <Check size={12} /> : <Link2 size={12} />}
          {copied ? 'Copiado!' : 'Copiar link'}
        </button>
      </div>
    </div>
  )
}
